import { ViewportScroller, DOCUMENT } from '@angular/common';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';

import {
  RlaAction,
  RlaGuards,
  RouteLinkAction,
} from '../../../types/route-link-action';
import { bgInOut, linksInOut, wrapperIn } from './main-menu-animations';

@Component({
    selector: 'lui-main-menu',
    templateUrl: './main-menu.component.html',
    styleUrls: ['./main-menu.component.scss'],
    animations: [wrapperIn, bgInOut, linksInOut],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MainMenuComponent implements OnInit, OnDestroy {
  @Input() get opened(): boolean {
    return this._opened;
  }
  set opened(open: BooleanInput) {
    this._opened = coerceBooleanProperty(open);
  }
  _opened = false;

  @Input() links!: RouteLinkAction[] | null;

  @Output() linkClick = new EventEmitter<RouteLinkAction>();

  @Output() openedChange = new EventEmitter<boolean>();

  _destroy$ = new Subject<void>();

  isRoute = RlaGuards.isRoute;

  isLink = RlaGuards.isLink;

  isAction = RlaGuards.isAction;

  constructor(
    private router: Router,
    private scroller: ViewportScroller,
    @Optional() @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        takeUntil(this._destroy$),
        filter((event) => event instanceof NavigationStart && this.opened)
      )
      .subscribe(() => {
        this.close();
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  open(): void {
    this.toggle(true);
  }

  close(): void {
    this.toggle(false);
  }

  toggle(open: boolean = !this.opened): void {
    this._opened = open;
    if (this.document) {
      if (open) {
        this.scroller.scrollToPosition([0, 0]);

        this.renderer.setStyle(this.document.body, 'height', '100vh');
        this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
      } else {
        this.renderer.removeStyle(this.document.body, 'height');
        this.renderer.removeStyle(this.document.body, 'overflow');
      }
    }
    this.openedChange.emit(open);
    this.cdr.markForCheck();
  }

  onLinkClick(): void {
    this.close();
  }

  onActionClick(action: RlaAction): void {
    this.close();
    action.cb();
    this.linkClick.emit(action);
  }
}
